(() => {
const DEFAULT_CURRENCY_CODES = ["USD", "EUR", "GBP", "CAD", "JPY"];
const NON_CURRENCY_CODES = new Set(["MIN", "MAX", "AVG", "LOW", "HIGH"]);
const COUNTRY_TO_CURRENCY_FALLBACK = {
    US: "USD",
    CA: "CAD",
    GB: "GBP",
    UK: "GBP",
    JP: "JPY",
};

const poPlpFiltersSortDebugEnabled = () => {
    if (typeof window === "undefined") return false;

    try {
        const url = new URL(window.location.href);
        const debugParam = url.searchParams.get("po_sort_debug");

        if (debugParam === "1" || debugParam === "true") {
            sessionStorage.setItem("po_sort_debug", "1");
            window.__PO_PLP_SORT_DEBUG__ = true;
            return true;
        }

        if (debugParam === "0" || debugParam === "false") {
            sessionStorage.removeItem("po_sort_debug");
            window.__PO_PLP_SORT_DEBUG__ = false;
            return false;
        }
    } catch (_error) {
        // no-op
    }

    if (window.__PO_PLP_SORT_DEBUG__ === true) {
        return true;
    }

    try {
        return sessionStorage.getItem("po_sort_debug") === "1";
    } catch (_error) {
        return false;
    }
};

const poPlpFiltersSortDebug = (scope, payload = {}) => {
    if (!poPlpFiltersSortDebugEnabled()) {
        return;
    }

    window.poPlpSortDebugTools?.record("filters", scope, payload);
    console.log(`[PO sort debug][filters] ${scope}`, payload);
};

const getRenderedSortOptionsSnapshot = (root) =>
    Array.from(root?.querySelectorAll?.('input[name="sort_by"]') || []).map((input) => ({
        value: input.value || "",
        checked: input.checked,
        label: input.closest("label")?.innerText?.trim() || "",
    }));

const normalizeCurrency = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed ? trimmed.toUpperCase() : null;
};

const normalizeCountryCode = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return /^[A-Z]{2}$/i.test(trimmed) ? trimmed.toUpperCase() : null;
};

const getActiveCountry = () => {
    const htmlLangCountry = document?.documentElement?.lang?.split?.("-")?.[1];
    const localeCountry = window.SHOP_LOCALES?.current?.iso_code?.split?.("-")?.[1];
    const candidates = [
        window.Shopify?.country,
        document?.documentElement?.dataset?.countryCode,
        htmlLangCountry,
        localeCountry,
    ];

    for (const candidate of candidates) {
        const normalized = normalizeCountryCode(candidate);
        if (normalized) return normalized;
    }

    return null;
};

const getActiveCurrency = () => {
    const candidates = [
        window.Shopify?.currency?.active,
        window.Shopify?.currency?.currency_code,
        window.Shopify?.currency?.iso_code,
        window.ShopifyAnalytics?.meta?.currency,
        window.Shopify?.analytics?.meta?.currency,
        window.algoliaShopify?.shopify?.currency,
        window.algoliaShopify?.shopify?.market?.currency,
        window.algoliaShopify?.shopify?.market?.currency_code,
        window.algoliaShopify?.shopify?.market?.currencyCode,
    ];

    for (const candidate of candidates) {
        const normalized = normalizeCurrency(candidate);
        if (normalized) return normalized;
    }

    const fallbackCountry = getActiveCountry();
    return COUNTRY_TO_CURRENCY_FALLBACK[fallbackCountry] || (fallbackCountry ? "EUR" : null);
};

const getCurrencyCodesFromSortOrders = () => {
    const options = window.algoliaShopify?.sortOrders;
    if (!Array.isArray(options)) return [];
    const codes = new Set();

    options.forEach(({ value = "" }) => {
        const match = String(value).match(/(?:pricing|price)_([a-z]{3})/i);
        if (match?.[1]) {
            codes.add(match[1].toUpperCase());
        }
    });

    return Array.from(codes);
};

const getCurrencyCodes = (activeCurrency) => {
    const codes = new Set(DEFAULT_CURRENCY_CODES);
    getCurrencyCodesFromSortOrders().forEach((code) => codes.add(code));
    if (activeCurrency) codes.add(activeCurrency);
    return Array.from(codes);
};

const getCurrencyFromFacetName = (name, currencyCodes) => {
    if (typeof name !== "string") return null;
    const normalized = name.toLowerCase();

    for (const code of currencyCodes) {
        const lower = code.toLowerCase();
        const pattern = new RegExp(
            `(?:^|[^a-z0-9])(?:price|pricing|market_pricing|marketpricing)[^a-z0-9]*${lower}(?:[^a-z0-9]|$)|` +
            `(?:^|[^a-z0-9])${lower}[^a-z0-9]*(?:price|pricing)(?:[^a-z0-9]|$)`,
            "i"
        );

        if (pattern.test(normalized)) return code;
    }

    const fallbackMatch =
        normalized.match(
            /(?:^|[^a-z0-9])(?:price|pricing|market_pricing|marketpricing)[^a-z0-9]*([a-z]{3})(?:[^a-z0-9]|$)/i
        ) ||
        normalized.match(
            /(?:^|[^a-z0-9])([a-z]{3})[^a-z0-9]*(?:price|pricing)(?:[^a-z0-9]|$)/i
        );

    if (fallbackMatch?.[1]) {
        const code = fallbackMatch[1].toUpperCase();
        if (!NON_CURRENCY_CODES.has(code)) {
            return code;
        }
    }

    return null;
};

const filterFacetsByCurrency = (facets, activeCurrency) => {
    if (!Array.isArray(facets)) return facets;
    const normalizedActive = normalizeCurrency(activeCurrency);
    if (!normalizedActive) return facets;

    const currencyCodes = getCurrencyCodes(normalizedActive);
    return facets.filter((facet) => {
        const name = typeof facet === "string" ? facet : facet?.name;
        const code = getCurrencyFromFacetName(name, currencyCodes);
        return !code || normalizeCurrency(code) === normalizedActive;
    });
};

const normalizeSortOrderValue = (value) => {
    if (typeof value !== "string") return "";
    const normalized = value.trim().toLowerCase();
    const markerIndex = normalized.indexOf("products");
    return markerIndex >= 0 ? normalized.slice(markerIndex) : normalized;
};

const filterSortOrdersByAllowlist = (options) => {
    if (!Array.isArray(options)) return options;
    const allowlist = window.poAlgoliaSortOrderAllowlist;
    if (!Array.isArray(allowlist) || allowlist.length === 0) return options;

    const allowed = new Set(allowlist.map(normalizeSortOrderValue).filter(Boolean));
    const filtered = options.filter((option) => {
        const value = typeof option === "string" ? option : option?.value;
        const normalized = normalizeSortOrderValue(value);
        return normalized && allowed.has(normalized);
    });

    return filtered.length > 0 ? filtered : options;
};

const sanitizeFilterLookupValue = (value) =>
    String(value || "")
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .trim();

const normalizeFilterLookupValue = (value) =>
    String(value || "")
        .trim()
        .toLowerCase();

const normalizeFacetNameKey = (value) =>
    String(value || "")
        .trim()
        .toLowerCase();

const normalizeFacetTitleKey = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

const FACET_CANONICAL_MAP = {
    "gender": "gender",
    "meta.custom.gender": "gender",
    "frame_color": "frame_color",
    "front_inner_color_facet": "frame_color",
    "face_coverage": "fit",
    "frame_fit": "fit",
    "lens_size": "fit",
    "lens_type": "lens_material",
    "meta.custom.color": "frame_color",
    "meta.custom.front_inner_color_facet": "frame_color",
    "meta.custom.temple_color": "frame_color",
    "meta.custom.temple_color_facet": "frame_color",
    "frame_shape": "frame_shape",
    "meta.custom.frame_shape": "frame_shape",
    "meta.custom.frame_shape_facet": "frame_shape",
    "frame_material": "frame_material",
    "material_ilter": "frame_material",
    "meta.custom.material": "frame_material",
    "meta.custom.material_ilter": "frame_material",
    "meta.custom.material_filter": "frame_material",
    "lens_color": "lens_color",
    "meta.custom.lens_color": "lens_color",
    "meta.custom.lens_color_description": "lens_color",
    "meta.custom.lens_color_description_filter": "lens_color",
    "lens_treatment": "lens_treatment",
    "meta.custom.lens_treatment": "lens_treatment",
    "meta.custom.lens_treatment_filter": "lens_treatment",
    "lens_material": "lens_material",
    "lens_base_coloring_type_filter": "lens_material",
    "meta.custom.lens_base_coloring_type_filter": "lens_material",
    "meta.custom.lens_type": "lens_material",
    "meta.custom.lens_material": "lens_material",
    "fit": "fit",
    "meta.custom.face_coverage": "fit",
    "meta.custom.frame_fit": "fit",
    "meta.custom.lens_size": "fit",
    "meta.custom.fit": "fit",
    "geofit": "geofit",
    "meta.custom.geofit": "geofit",
    "meta.custom.bridge_nosepads": "geofit",
};

const FACET_TITLE_CANONICAL_MAP = {
    "gender": "gender",
    "frame color": "frame_color",
    "frame colour": "frame_color",
    "frame shape": "frame_shape",
    "frame material": "frame_material",
    "material filter": "frame_material",
    "lens color": "lens_color",
    "lens colour": "lens_color",
    "lens treatment": "lens_treatment",
    "lens type": "lens_material",
    "lens material": "lens_material",
    "face coverage": "fit",
    "frame fit": "fit",
    "lens size": "fit",
    "fit": "fit",
    "bridge and nosepads": "geofit",
    "bridge choice & nosepads": "geofit",
    "bridge choice and nosepads": "geofit",
};

const FACET_ALIAS_PREFERENCE_MAP = {
    gender: ["meta.custom.gender", "gender"],
    frame_color: ["meta.custom.front_inner_color_facet", "meta.custom.temple_color_facet", "meta.custom.temple_color", "meta.custom.color", "front_inner_color_facet", "frame_color"],
    frame_shape: ["meta.custom.frame_shape_facet", "meta.custom.frame_shape", "frame_shape"],
    frame_material: ["meta.custom.material_ilter", "meta.custom.material_filter", "meta.custom.material", "material_ilter", "frame_material"],
    lens_color: ["meta.custom.lens_color_description_filter", "meta.custom.lens_color_description", "meta.custom.lens_color", "lens_color"],
    lens_treatment: ["meta.custom.lens_treatment_filter", "meta.custom.lens_treatment", "lens_treatment"],
    lens_material: ["meta.custom.lens_material", "meta.custom.lens_base_coloring_type_filter", "meta.custom.lens_type", "lens_base_coloring_type_filter", "lens_material", "lens_type"],
    fit: ["meta.custom.fit", "meta.custom.face_coverage", "meta.custom.frame_fit", "meta.custom.lens_size", "fit", "face_coverage", "frame_fit", "lens_size"],
    geofit: ["meta.custom.geofit", "meta.custom.bridge_nosepads", "geofit", "bridge_nosepads"],
};

const getFacetCanonicalKey = (value) => {
    const normalized = normalizeFacetNameKey(value);

    if (FACET_CANONICAL_MAP[normalized]) {
        return FACET_CANONICAL_MAP[normalized];
    }

    if (!normalized) {
        return "";
    }

    if (normalized.includes("temple_color")) return "frame_color";
    if (normalized.includes("front_inner_color")) return "frame_color";
    if (normalized.endsWith(".color") || normalized === "color") return "frame_color";
    if (normalized.includes("frame_shape")) return "frame_shape";
    if (normalized.includes("material_ilter")) return "frame_material";
    if (normalized.includes("material_filter") || normalized.endsWith(".material")) return "frame_material";
    if (normalized.includes("lens_color")) return "lens_color";
    if (normalized.includes("lens_treatment")) return "lens_treatment";
    if (normalized.includes("lens_base_coloring_type")) return "lens_material";
    if (normalized.includes("lens_type")) return "lens_material";
    if (normalized.includes("lens_material")) return "lens_material";
    if (normalized.includes("lens_size")) return "fit";
    if (normalized.includes("frame_fit")) return "fit";
    if (normalized.includes("face_coverage")) return "fit";
    if (normalized.includes("bridge_nosepads") || normalized.endsWith(".geofit")) return "geofit";
    if (normalized.endsWith(".fit") || normalized === "fit") return "fit";
    if (normalized.endsWith(".gender") || normalized === "gender") return "gender";

    const stripped = normalized
        .replace(/^meta\.custom\./, "")
        .replace(/_description_filter$/, "")
        .replace(/_description$/, "")
        .replace(/_facet$/, "")
        .replace(/_filter$/, "");

    return FACET_CANONICAL_MAP[stripped] || stripped;
};

const getFacetAliasPreferenceIndex = (facetName) => {
    const canonicalKey = getFacetCanonicalKey(facetName);
    const preferredNames = FACET_ALIAS_PREFERENCE_MAP[canonicalKey] || [];
    const normalizedFacetName = normalizeFacetNameKey(facetName);
    const preferenceIndex = preferredNames.findIndex((name) => normalizeFacetNameKey(name) === normalizedFacetName);

    return preferenceIndex === -1 ? Number.MAX_SAFE_INTEGER : preferenceIndex;
};

const getRenderableFacetKey = (facetName) =>
    getFacetCanonicalKey(facetName) || normalizeFacetNameKey(facetName);

const isKnownFacetCanonicalKey = (value) =>
    Boolean(value) && Object.prototype.hasOwnProperty.call(FACET_ALIAS_PREFERENCE_MAP, value);

const isUsableFacetFallbackKey = (value) => {
    const normalized = normalizeFacetNameKey(value);

    return Boolean(normalized) && /[a-z]/.test(normalized);
};

const getFacetConfigKey = (facetName, fallbackTitle = "") => {
    const canonicalByName = getFacetCanonicalKey(facetName);

    if (isKnownFacetCanonicalKey(canonicalByName)) {
        return canonicalByName;
    }

    const canonicalByTitle = FACET_TITLE_CANONICAL_MAP[normalizeFacetTitleKey(fallbackTitle)];

    return canonicalByTitle || canonicalByName || normalizeFacetNameKey(facetName) || normalizeFacetTitleKey(fallbackTitle);
};

const getNumericShopifyResourceId = (value) => {
    const match = String(value || "").match(/\/(\d+)$/);
    return match?.[1] || "";
};

const normalizeMetaObjectFieldKey = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_");

const extractMetaobjectGid = (value) => {
    const rawValue = String(value || "").trim();

    if (!rawValue) return "";
    if (/^gid:\/\/shopify\/metaobject\/\d+$/i.test(rawValue)) {
        return rawValue;
    }

    const idMatch = rawValue.match(/metaobject[^0-9]*(\d+)/i);
    return idMatch?.[1] ? `gid://shopify/Metaobject/${idMatch[1]}` : "";
};

const humanizeFilterValue = (value) =>
    String(value || "")
        .trim()
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (match) => match.toUpperCase());

const chunkArray = (items, size) => {
    if (!Array.isArray(items) || size <= 0) return [];

    const chunks = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
};


const normalizeSortLabel = (label) =>
    String(label || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .replaceAll(" ", "_");

const normalizeSortValue = (value) => String(value || "").trim().toLowerCase();
const normalizeQueryIndexName = (value) =>
    window.poAlgoliaUtils?.normalizeIndexName?.(value) || String(value || "").trim();
const normalizeSortOption = (option = {}) => {
    if (typeof option === "string") {
        return {
            label: option,
            value: normalizeQueryIndexName(option),
        };
    }

    return {
        ...option,
        value: normalizeQueryIndexName(option?.value),
    };
};

const isPriceAscValue = (value) => {
    const v = normalizeSortValue(value);
    return v.includes("price_asc") || v.includes(".price_asc");
};

const isPriceDescValue = (value) => {
    const v = normalizeSortValue(value);
    return v.includes("price_desc") || v.includes(".price_desc");
};

const isPricingValue = (value) => {
    const v = normalizeSortValue(value);
    return v.includes("pricing_") || v.includes("market_pricing") || v.includes("marketpricing") || v.includes(".price_");
};

const isBaseProductsIndexValue = (value) => {
    const v = normalizeSortValue(value);
    if (!v) return false;
    if (!v.includes("shopify_products")) return false;
    if (isPriceAscValue(v) || isPriceDescValue(v)) return false;
    if (v.includes("published_at") || v.includes("recently_ordered") || v.includes("ordered") || v.includes("newreleases")) return false;
    if (v.includes("market_pricing") && v.includes(".price_")) return false;
    return true;
};

const pickFirst = (arr, predicate) => (Array.isArray(arr) ? arr.find(predicate) : undefined);

const pickBestPriceSortOption = (options, direction, country, activeCurrencyLower) => {
    const wantsAsc = direction === "asc";
    const matcher = wantsAsc ? isPriceAscValue : isPriceDescValue;
    const candidates = (Array.isArray(options) ? options : []).filter((o) => matcher(o?.value));
    if (candidates.length === 0) return undefined;

    const normalizedCountry = String(country || "").toUpperCase();
    if (normalizedCountry === "US") {
        const genericUSReplica = pickFirst(candidates, (o) => {
            const v = normalizeSortValue(o.value);
            return v.endsWith(`products_price_${direction}`);
        });
        if (genericUSReplica) return genericUSReplica;
    }

    if (activeCurrencyLower) {
        const byMarketPricing = pickFirst(candidates, (o) => {
            const v = normalizeSortValue(o.value);
            return v.includes(`market_pricing_${activeCurrencyLower}`) || v.includes(`pricing_${activeCurrencyLower}`);
        });
        if (byMarketPricing) return byMarketPricing;
    }

    const anyPricing = pickFirst(candidates, (o) => isPricingValue(o.value));
    return anyPricing || candidates[0];
};

const pickMostPopularOption = (options, currentMarket) => {
    const normalized = (Array.isArray(options) ? options : []).map((o) => ({
        ...o,
        _nLabel: normalizeSortLabel(o?.label),
        _nValue: normalizeSortValue(o?.value),
    }));

    let best = normalized.find((o) => o._nLabel === "most_popular" || o._nLabel.includes("most_popular"));

    if (!best) {
        best = normalized.find((o) => o._nValue.includes("recently_ordered_count_desc") || o._nValue.includes("best_seller") || o._nValue.includes("bestseller"));
    }

    if (!best) {
        best = normalized.find((o) => o._nValue.includes("newreleases") || o._nLabel.includes("newreleases"));
    }

    if (best && currentMarket?.suffix) {
        const suffix = String(
            window.poAlgoliaUtils?.normalizeMarketSuffix?.(currentMarket.suffix) || currentMarket.suffix
        ).trim();
        if (suffix) {
            const suffixCandidates = [
                `production_products_${suffix}_recently_ordered_count_desc`,
                `production_products_${suffix}_most_popular`,
                `production_products_${suffix}_newreleases`,
            ].map((v) => v.toLowerCase());

            const suffixMatch = normalized.find((o) => suffixCandidates.includes(o._nValue));
            if (suffixMatch) {
                best = { ...best, value: suffixMatch.value };
            }
        }
    }

    return best;
};

const narrowSortOrdersForPlp = (options, { country, activeCurrencyLower, currentMarket } = {}) => {
    if (!Array.isArray(options) || options.length === 0) return options;

    const relevance =
        pickFirst(options, (o) => normalizeSortLabel(o?.label) === "relevance") ||
        pickFirst(options, (o) => isBaseProductsIndexValue(o?.value)) ||
        options[0];

    //const mostPopular = pickMostPopularOption(options, currentMarket);

    const mostExpensive = pickBestPriceSortOption(options, "desc", country, activeCurrencyLower);
    const cheapest = pickBestPriceSortOption(options, "asc", country, activeCurrencyLower);

    const uniq = new Map();
    [relevance, mostExpensive, cheapest]
        .filter(Boolean)
        .forEach((opt) => {
            const key = normalizeSortValue(opt.value);
            if (!uniq.has(key)) uniq.set(key, opt);
        });

    return Array.from(uniq.values());
};

!customElements.get('po-plp-filters') && customElements.define('po-plp-filters', class extends HTMLElement {
    connectedCallback() {
        this.activeCurrency = getActiveCurrency();
        this.dialog = this.closest("dialog");
        this.displayFacets = [];
        this.facets = [];
        this.lastFilters = null;
        this.lastIndex = null;
        this.maxHits = 0;
        this.widgets = [];
        this.multiChoiseTemplate = this.querySelector("[data-multichoice-acc]");
        this.checkboxOptionTemplate = this.querySelector("[data-checkbox-option]");
        this.form = this.querySelector("form");
        this.resultCountDisplay = this.querySelector("[data-result-count]");
        this.filterBy = this.form?.elements.namedItem("filter_by");
        this.resetButton = this.querySelector("[data-reset-filters]");
        this.sortOptionTemplate = this.querySelector("[data-sort-option]");
        this.sortByContent = this.querySelector("[data-sort-by-content]");
        this.dialogDismissTrigger = this.querySelector("po-modal-closer, .js-po-modal-close");

        this.config = JSON.parse(this.querySelector("script[type='application/json']")?.textContent || "{}");
        this.facetTitles = this.config.titles || {};
        this.facetTitleMap = new Map(
            Object.entries(this.facetTitles).map(([key, value]) => [normalizeFacetNameKey(key), value])
        );
        this.facetOrder = Array.isArray(this.config.facetOrder) ? this.config.facetOrder : [];
        this.facetOrderMap = new Map(
            this.facetOrder.map((name, index) => [normalizeFacetNameKey(name), index])
        );
        this.facetOrderCanonicalKeys = this.facetOrder
            .map((name) => getFacetCanonicalKey(name) || normalizeFacetNameKey(name))
            .filter((name, index, items) => Boolean(name) && items.indexOf(name) === index);
        this.facetOrderCanonicalKeySet = new Set(this.facetOrderCanonicalKeys);

        this.facetsBlacklist = this.config.blacklist || [];
        this.facetDescriptions = this.config.facetDescriptions || {}; 
        this.filtersObjects = [];
        this.filterObjectsMap = new Map();
        this.plpFiltersLoaded = false;
        this.widgetElementsByFacet = new Map();
        this.widgetsInitialized = false;
        this.pendingWidgetRefresh = false;
        this.filterMetaPromise = null;
        this.filterMetaPrefetchId = null;
        this.pendingFilterMetaIds = new Set();
        this.liveSubmitTimer = null;
        this.configuredFacetsPromise = null;
        this.availableFacetsPromise = null;
        this.dialogOpenObserver = null;
        this.dialogCloseAction = null;
        this.appliedState = null;
        this.initialUrlState = this.getInitialUrlState();
        this.initialUrlStateApplied = false;

        if (this.form) {
            this.form.addEventListener("submit", this.form_submitHandler);
            this.form.addEventListener("reset", this.form_resetHandler);
            this.form.addEventListener("change", this.form_changedHandler);
        }
        this.resetButton?.addEventListener("click", this.resetButton_clickHandler);
        this.dialogDismissTrigger?.addEventListener("click", this.dialogDismissTrigger_clickHandler, true);
        document.addEventListener("po:algolia:sort-orders-updated", this.sortOrdersUpdated_handler);

        document.addEventListener("plp:facets-loaded", this.plpFacetsLoaded_handler);
        document.addEventListener("plp:clear-filters", this.plpResetFilters_handler);
        document.addEventListener("plp:open-filters", this.plpOpenFilters_handler);
        document.addEventListener("plp:facets-updated", this.plpFacetsUpdated_handler);
        document.addEventListener("plp:facet-count-updated", this.plpFacetsCountUpdated_handler);
        if (this.dialog) {
            this.dialog.addEventListener("cancel", this.dialog_cancelHandler);
            this.dialog.addEventListener("close", this.dialog_closeHandler);
            this.dialog.addEventListener("click", this.dialog_clickCaptureHandler, true);
            this.dialogOpenObserver = new MutationObserver(() => {
                if (this.dialog?.hasAttribute("open")) {
                    this.handleDialogOpened();
                }
            });
            this.dialogOpenObserver.observe(this.dialog, {
                attributes: true,
                attributeFilter: ["open"],
            });
        }

        this.updateSortByOptions();
        this.commitAppliedState(this.getCurrentFacetFilters(), this.getCurrentAppliedSortValue());
        this.scheduleFilterMetaPrefetch();
    }

    disconnectedCallback() {
        if (this.form) {
            this.form.removeEventListener("submit", this.form_submitHandler);
            this.form.removeEventListener("reset", this.form_resetHandler);
            this.form.removeEventListener("change", this.form_changedHandler);
        }
        this.resetButton?.removeEventListener("click", this.resetButton_clickHandler);
        this.dialogDismissTrigger?.removeEventListener("click", this.dialogDismissTrigger_clickHandler, true);
        document.removeEventListener("po:algolia:sort-orders-updated", this.sortOrdersUpdated_handler);

        document.removeEventListener("plp:facets-loaded", this.plpFacetsLoaded_handler);
        document.removeEventListener("plp:clear-filters", this.plpResetFilters_handler);
        document.removeEventListener("plp:open-filters", this.plpOpenFilters_handler);
        document.removeEventListener("plp:facets-updated", this.plpFacetsUpdated_handler);
        document.removeEventListener("plp:facet-count-updated", this.plpFacetsCountUpdated_handler);
        this.dialog?.removeEventListener("cancel", this.dialog_cancelHandler);
        this.dialog?.removeEventListener("close", this.dialog_closeHandler);
        this.dialog?.removeEventListener("click", this.dialog_clickCaptureHandler, true);
        this.dialogOpenObserver?.disconnect();
        this.dialogOpenObserver = null;

        this.clearScheduledFilterMetaPrefetch();
        window.clearTimeout(this.liveSubmitTimer);
        this.widgets = [];
        this.facets = [];
        this.displayFacets = [];
        this.filtersObjects = [];
        this.filterObjectsMap.clear();
        this.pendingFilterMetaIds.clear();
        this.widgetElementsByFacet.clear();
        this.configuredFacetsPromise = null;
        this.availableFacetsPromise = null;
    }

    normalizeFacetFiltersState (facetFilters = []) {
        if (!Array.isArray(facetFilters)) {
            return [];
        }

        return facetFilters
            .map((entry) => {
                if (Array.isArray(entry)) {
                    const normalizedGroup = entry
                        .map((value) => String(value || "").trim())
                        .filter(Boolean);

                    return normalizedGroup.length > 0 ? normalizedGroup : null;
                }

                const normalizedValue = String(entry || "").trim();
                return normalizedValue ? normalizedValue : null;
            })
            .filter(Boolean);
    }

    serializeFacetFiltersState (facetFilters = []) {
        return JSON.stringify(
            this.normalizeFacetFiltersState(facetFilters)
                .map((entry) => (
                    Array.isArray(entry)
                        ? [...entry].sort((left, right) => left.localeCompare(right))
                        : [entry]
                ))
                .sort((left, right) => left.join("|").localeCompare(right.join("|")))
        );
    }

    buildFacetFiltersFromFormData (formData) {
        const facetsFilters = [];
        const uniqueKeys = new Set();

        for (const key of formData.keys()) {
            if (uniqueKeys.has(key)) {
                continue;
            }

            uniqueKeys.add(key);
            const choices = formData.getAll(key);

            if (choices.length > 0 && !key.startsWith("_") && key !== "sort_by") {
                facetsFilters.push(choices.map((entry) => `${key}:${entry}`));
            }
        }

        return facetsFilters;
    }

    isIgnoredUrlParam (name = "") {
        const normalizedName = String(name || "").trim().toLowerCase();

        return !normalizedName || [
            "page",
            "q",
            "view",
            "sort_by",
            "section_id",
            "country",
            "locale",
            "search_mode",
            "show_variant",
            "ungrouped",
            "po_plp_filtered",
            "po_sort_debug",
        ].includes(normalizedName)
            || normalizedName.startsWith("utm_")
            || normalizedName.startsWith("_");
    }

    normalizeUrlFacetParamName (name = "") {
        const rawName = String(name || "").trim();

        if (!rawName) {
            return "";
        }

        if (rawName.startsWith("filter.p.m.custom.")) {
            return `meta.custom.${rawName.slice("filter.p.m.custom.".length)}`;
        }

        return rawName;
    }

    isPotentialFacetQueryParam (name = "") {
        if (this.isIgnoredUrlParam(name)) {
            return false;
        }

        const rawName = String(name || "").trim();
        const resolvedName = this.normalizeUrlFacetParamName(rawName);
        const normalizedName = normalizeFacetNameKey(resolvedName);
        if (!normalizedName) {
            return false;
        }

        if (rawName.startsWith("filter.")) {
            return true;
        }

        if (normalizedName.startsWith("meta.custom.")) {
            return true;
        }

        if (isKnownFacetCanonicalKey(getFacetCanonicalKey(normalizedName))) {
            return true;
        }

        return Boolean(
            this.form?.elements?.namedItem(rawName)
            || this.form?.elements?.namedItem(resolvedName)
        );
    }

    getInitialUrlState () {
        if (typeof window === "undefined") {
            return this.buildFilterState();
        }

        let params;

        try {
            params = new URLSearchParams(window.location.search || "");
        } catch (_error) {
            return this.buildFilterState();
        }

        const facetFilters = [];
        const processedKeys = new Set();
        const rawSortBy = params.get("sort_by") || "";

        Array.from(params.keys()).forEach((key) => {
            if (processedKeys.has(key) || !this.isPotentialFacetQueryParam(key)) {
                return;
            }

            processedKeys.add(key);
            const resolvedKey = this.normalizeUrlFacetParamName(key);

            const values = params
                .getAll(key)
                .map((value) => String(value || "").trim())
                .filter(Boolean);

            if (values.length > 0) {
                facetFilters.push(values.map((value) => `${resolvedKey}:${value}`));
            }
        });

        return {
            ...this.buildFilterState({
                facetFilters,
                sortBy: rawSortBy || this.getDefaultSortValue(),
            }),
            hasUrlSortBy: Boolean(rawSortBy),
        };
    }

    hasInitialUrlState () {
        const urlState = this.initialUrlState;

        if (!urlState) {
            return false;
        }

        return urlState.facetFilters.length > 0 || urlState.hasUrlSortBy === true;
    }

    getCurrentAppliedSortValue () {
        return (
            window.__poPlpCurrentIndexName ||
            window.poAlgoliaUtils?.getLastIndexName?.() ||
            this.lastIndex ||
            window.algoliaShopify?.instantsearch?.search?.helper?.state?.index ||
            window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index ||
            this.getDefaultSortValue()
        );
    }

    buildFilterState ({ facetFilters = [], sortBy = "" } = {}) {
        const normalizedFacetFilters = this.normalizeFacetFiltersState(facetFilters);
        const resolvedSortBy = String(sortBy || this.getDefaultSortValue() || "").trim();

        return {
            facetFilters: normalizedFacetFilters,
            serializedFacetFilters: this.serializeFacetFiltersState(normalizedFacetFilters),
            sortBy: resolvedSortBy,
        };
    }

    getCurrentDraftState () {
        if (!this.form) {
            return this.buildFilterState({
                facetFilters: this.getCurrentFacetFilters(),
                sortBy: this.getCurrentAppliedSortValue(),
            });
        }

        const formData = new FormData(this.form);

        return this.buildFilterState({
            facetFilters: this.buildFacetFiltersFromFormData(formData),
            sortBy: formData.get("sort_by") || this.getCurrentAppliedSortValue(),
        });
    }

    commitAppliedState (facetFilters = [], sortBy = "") {
        this.appliedState = this.buildFilterState({
            facetFilters,
            sortBy,
        });
    }

    buildFacetSelectionMap (facetFilters = []) {
        const selectionMap = new Map();

        this.normalizeFacetFiltersState(facetFilters).forEach((entry) => {
            const filters = Array.isArray(entry) ? entry : [entry];

            filters.forEach((filterValue) => {
                const separatorIndex = filterValue.indexOf(":");

                if (separatorIndex === -1) {
                    return;
                }

                const facetName = filterValue.slice(0, separatorIndex);
                const optionValue = filterValue.slice(separatorIndex + 1);

                if (!selectionMap.has(facetName)) {
                    selectionMap.set(facetName, new Set());
                }

                selectionMap.get(facetName).add(optionValue);
            });
        });

        return selectionMap;
    }

    syncFieldsetCounters () {
        this.form?.querySelectorAll("fieldset").forEach((fieldset) => {
            const counterDisplay = fieldset.querySelector("[data-acc-counter]");

            if (!counterDisplay) {
                return;
            }

            const checkedCount = fieldset.querySelectorAll("input[type='checkbox']:checked").length;
            this.updateCounterDisplay(counterDisplay, checkedCount);
        });
    }

    applyStateToForm (state) {
        if (!this.form || !state) {
            return;
        }

        const facetSelectionMap = this.buildFacetSelectionMap(state.facetFilters);
        const normalizedSortBy = normalizeSortValue(state.sortBy || this.getDefaultSortValue());

        this.form.querySelectorAll("input[type='checkbox']").forEach((input) => {
            const selectedValues = facetSelectionMap.get(input.name);
            const isSelected = Boolean(selectedValues?.has(String(input.value)));

            input.checked = isSelected;
            input.defaultChecked = isSelected;
        });

        const sortInputs = Array.from(this.form.querySelectorAll("input[name='sort_by']"));
        let hasSelectedSort = false;

        sortInputs.forEach((input) => {
            const isSelected = normalizeSortValue(input.value) === normalizedSortBy;
            input.checked = isSelected;
            input.defaultChecked = isSelected;
            hasSelectedSort = hasSelectedSort || isSelected;
        });

        if (!hasSelectedSort) {
            const fallbackSortValue = this.getDefaultSortValue();

            sortInputs.forEach((input) => {
                const isSelected = normalizeSortValue(input.value) === normalizeSortValue(fallbackSortValue);
                input.checked = isSelected;
                input.defaultChecked = isSelected;
            });
        }

        this.syncFieldsetCounters();

        if (this.widgetsInitialized) {
            this.refreshRenderedWidgets();
        }

        this.updateWidgets({ includeDynamicFacets: true });
    }

    hasDraftChangesComparedToApplied () {
        if (!this.appliedState) {
            return false;
        }

        const draftState = this.getCurrentDraftState();

        return (
            draftState.serializedFacetFilters !== this.appliedState.serializedFacetFilters
            || normalizeSortValue(draftState.sortBy) !== normalizeSortValue(this.appliedState.sortBy)
        );
    }

    restoreAppliedState ({ syncPreview = true } = {}) {
        if (!this.appliedState) {
            this.commitAppliedState(this.getCurrentFacetFilters(), this.getCurrentAppliedSortValue());
        }

        if (!this.appliedState) {
            return;
        }

        window.clearTimeout(this.liveSubmitTimer);
        this.applyStateToForm(this.appliedState);

        if (syncPreview) {
            this.dispatchFiltersChanged(
                this.appliedState.facetFilters,
                this.appliedState.sortBy || null,
                false
            );
        }
    }

    setDialogCloseAction (action = null) {
        this.dialogCloseAction = action;
    }

    findMetaObjectByAliasHandles (name, sanitizedValue) {
        const canonicalKey = getFacetCanonicalKey(name);
        const aliases = FACET_ALIAS_PREFERENCE_MAP[canonicalKey];

        if (!aliases) return undefined;

        const valuePart = sanitizedValue.replaceAll(" ", "-").toLowerCase();

        for (const alias of aliases) {
            const prefix = alias.replace(/^meta\.custom\./, "").replaceAll("_", "-");
            const handle = `${prefix}-${valuePart}`;
            const obj = this.filterObjectsMap.get(handle);

            if (obj) return obj;
        }

        return undefined;
    }

    createCheckboxOption (template, name = "", value = "", count = 0) {
        const rawValue = String(value || "").trim();
        const sanitizedValue = sanitizeFilterLookupValue(rawValue);
        const normalizedValue = normalizeFilterLookupValue(rawValue);
        const numericResourceId = getNumericShopifyResourceId(rawValue);
        const metaobjectGid = extractMetaobjectGid(rawValue);
        const metaHandle = `${name.replace("meta.custom.", "").replaceAll("_", "-")}-${sanitizedValue.replaceAll(" ", "-")}`.toLowerCase();
        const simpleHandle = sanitizedValue.replaceAll(" ", "-").toLowerCase();

        // Support both handle-based facets and Shopify GID facet values.
        // Falls back to alias handles to cover cases where the Algolia facet name
        // differs from the metaobject handle prefix (e.g. bridge_nosepads vs geofit).
        const metaObj =
            this.filterObjectsMap.get(normalizeFilterLookupValue(metaobjectGid)) ||
            this.filterObjectsMap.get(normalizedValue) ||
            this.filterObjectsMap.get(sanitizedValue.toLowerCase()) ||
            this.filterObjectsMap.get(numericResourceId) ||
            this.filterObjectsMap.get(metaHandle) ||
            this.filterObjectsMap.get(simpleHandle) ||
            this.findMetaObjectByAliasHandles(name, sanitizedValue);

        const clone = template.content.cloneNode(true);

        const labelEl = clone.querySelector("[data-cb-label]");
        const checkbox = clone.querySelector("input[type='checkbox']");
        const iconEl = clone.querySelector("[data-cb-icon]");
        const descriptionEl = clone.querySelector("[data-cb-description]");

        if (labelEl) {
            labelEl.textContent = this.getMetaObjectLabel(metaObj, sanitizedValue);
        }

        if (checkbox) {
            checkbox.name = name;
            checkbox.value = value;
            checkbox.dataset.count = count;
            checkbox.dataset.metaHandle = metaHandle;
            checkbox.ariaLabel = labelEl?.textContent || sanitizedValue;
            checkbox.dataset.tealiumValue = rawValue.toLowerCase().replaceAll(" ", "");
        }

        if (iconEl) {
            const imageUrl = this.getMetaObjectImageUrl(metaObj);
            const colorValue = this.getMetaObjectColor(metaObj);

            if (imageUrl) {
                iconEl.style.backgroundImage = `url(${imageUrl})`;
                iconEl.style.backgroundColor = "";
                iconEl.removeAttribute("data-hidden");
            }
            else if (colorValue) {
                iconEl.style.backgroundColor = colorValue;
                iconEl.style.backgroundImage = "";
                iconEl.removeAttribute("data-hidden");
            }
            else {
                iconEl.style.backgroundImage = "";
                iconEl.style.backgroundColor = "";
                iconEl.toggleAttribute("data-hidden", true);
            }
        }

        clone.hasDescription = false;

        if (descriptionEl) {
            const metaDescription = this.getMetaObjectDescription(metaObj);
            const facetCanonicalKey = getFacetCanonicalKey(name);
            const hardcodedMap = this.facetDescriptions?.[facetCanonicalKey] || {};
            const normalize = (str) => normalizeFilterLookupValue(str).replace(/[\s_-]+/g, '');
            const hardcodedDescription = !metaDescription
                ? Object.entries(hardcodedMap).find(
                    ([key]) => normalize(key) === normalize(rawValue)
                )?.[1] ?? ''
                : '';
            const description = metaDescription || hardcodedDescription;

            if (description) {
                descriptionEl.textContent = description;
                descriptionEl.removeAttribute("data-hidden");
                clone.hasDescription = true;
            } else {
                descriptionEl.textContent = "";
                descriptionEl.toggleAttribute("data-hidden", true);
            }
        }

        return clone;
    }

  _getRawFacets() {
        const lastResults = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults;
        const rawFacets = lastResults?._rawResults?.[0]?.facets;

        if (rawFacets && Object.keys(rawFacets).length > 0) {
            return Object.entries(rawFacets).map(([name, data]) => ({ name, data }));
        }

        return lastResults?.facets || [];
    }

    isDialogOpen () {
        return Boolean(this.dialog?.open);
    }

    captureDynamicFacetSelections () {
        const selections = new Map();
        const renderedFacetKeys = new Set();

        this.form?.querySelectorAll("[data-facet]").forEach((facetElement) => {
            const facetKey = facetElement.dataset.facetKey || getFacetConfigKey(facetElement.dataset.facet);

            if (facetKey) {
                renderedFacetKeys.add(facetKey);
            }
        });

        this.form?.querySelectorAll("[data-facet] input:checked").forEach((input) => {
            const facetKey = input.closest("[data-facet]")?.dataset.facetKey || getFacetConfigKey(input.name);
            if (!facetKey) return;

            if (!selections.has(facetKey)) {
                selections.set(facetKey, new Set());
            }

            selections.get(facetKey).add(String(input.value));
        });

        const fallbackSelections = this.buildFacetSelectionMap(
            this.appliedState?.facetFilters || this.getCurrentFacetFilters()
        );

        fallbackSelections.forEach((values, facetName) => {
            const facetKey = getFacetConfigKey(facetName);

            if (!facetKey || renderedFacetKeys.has(facetKey) || selections.has(facetKey)) {
                return;
            }

            selections.set(facetKey, new Set(values));
        });

        return selections;
    }

    buildFilterObjectsMap (objects = []) {
        this.filterObjectsMap.clear();

        objects.forEach((object) => {
            this.indexFilterObject(object);
        });
    }

    indexFilterObject (object) {
        const keys = new Set();

        if (object?.handle) {
            keys.add(normalizeFilterLookupValue(object.handle));
        }

        if (object?.id) {
            keys.add(normalizeFilterLookupValue(object.id));

            const sanitizedId = sanitizeFilterLookupValue(object.id).toLowerCase();
            if (sanitizedId) {
                keys.add(sanitizedId);
            }

            const numericId = getNumericShopifyResourceId(object.id);
            if (numericId) {
                keys.add(numericId);
            }

            const metaobjectGid = extractMetaobjectGid(object.id);
            if (metaobjectGid) {
                keys.add(normalizeFilterLookupValue(metaobjectGid));
            }
        }

        keys.forEach((key) => {
            if (key) {
                this.filterObjectsMap.set(key, object);
            }
        });
    }

    mergeFilterObjects (objects = []) {
        const mergedObjects = new Map();

        this.filtersObjects.forEach((object) => {
            const key = normalizeFilterLookupValue(object?.id || object?.handle);
            if (key) {
                mergedObjects.set(key, object);
            }
        });

        objects.forEach((object) => {
            const key = normalizeFilterLookupValue(object?.id || object?.handle);
            if (key) {
                mergedObjects.set(key, object);
            }
        });

        this.filtersObjects = Array.from(mergedObjects.values());
        this.buildFilterObjectsMap(this.filtersObjects);
    }

    getMetaObjectField (object, keys = []) {
        const normalizedKeys = new Set(keys.map(normalizeMetaObjectFieldKey).filter(Boolean));
        return (Array.isArray(object?.fields) ? object.fields : []).find((field) => (
            normalizedKeys.has(normalizeMetaObjectFieldKey(field?.key))
        ));
    }

    getMetaObjectFieldValue (object, keys = []) {
        for (const key of keys) {
            const directField = object?.[key];
            if (typeof directField === "string" && directField.trim()) {
                return directField.trim();
            }

            if (directField?.value) {
                return String(directField.value).trim();
            }

            const field = this.getMetaObjectField(object, [key]);
            if (field?.value) {
                return String(field.value).trim();
            }
        }

        return "";
    }

    getMetaObjectFieldImageUrl (object, keys = []) {
        for (const key of keys) {
            const directField = object?.[key];
            const directImageUrl = directField?.reference?.image?.url;
            if (directImageUrl) {
                return directImageUrl;
            }

            const field = this.getMetaObjectField(object, [key]);
            const fieldImageUrl = field?.reference?.image?.url;
            if (fieldImageUrl) {
                return fieldImageUrl;
            }
        }

        return "";
    }

    getMetaObjectLabel (object, fallback = "") {
        const resolvedLabel =
            this.getMetaObjectFieldValue(object, ["label", "name", "title", "heading"]) ||
            (Array.isArray(object?.fields) ? object.fields.find((field) => {
                const normalizedKey = normalizeMetaObjectFieldKey(field?.key);
                return field?.value
                    && !["description", "image", "color"].includes(normalizedKey)
                    && !String(field.value).startsWith("gid://")
                    && !String(field.value).startsWith("#");
            })?.value : "");

        if (resolvedLabel) {
            return String(resolvedLabel).trim();
        }

        if (object?.handle) {
            return humanizeFilterValue(object.handle);
        }

        return fallback || "";
    }

    getMetaObjectDescription (object) {
        return this.getMetaObjectFieldValue(object, ["description", "subtitle", "sub_title", "copy", "content"]);
    }

    getMetaObjectImageUrl (object) {
        return this.getMetaObjectFieldImageUrl(object, ["image", "icon", "swatch"]);
    }

    getMetaObjectColor (object) {
        return this.getMetaObjectFieldValue(object, ["color", "hex", "swatch_color"]);
    }

    collectMetaobjectIdsFromFacets (facets = []) {
        const ids = new Set();

        facets.forEach((facet) => {
            Object.keys(facet?.data || {}).forEach((value) => {
                const metaobjectGid = extractMetaobjectGid(value);
                if (metaobjectGid) {
                    ids.add(metaobjectGid);
                }
            });
        });

        return Array.from(ids);
    }

    async fetchFilterMetaObjectsByIds (url, token, ids = []) {
        const country = window.Shopify?.country?.toUpperCase() || "US";
        const language = (window.Shopify?.locale || "EN").split("-")[0].toUpperCase();
        const objects = [];

        for (const idsChunk of chunkArray(ids, 50)) {
            const serializedIds = idsChunk.map((id) => JSON.stringify(id)).join(", ");
            const res = await Fetchutils.postGraphql(url, token, `#graphql
                query loadPlpFilterMetaByIds @inContext(country: ${country}, language: ${language}) {
                    nodes(ids: [${serializedIds}]) {
                        ... on Metaobject {
                            id
                            handle
                            type
                            label: field(key: "label") {
                                value
                            }
                            name: field(key: "name") {
                                value
                            }
                            title: field(key: "title") {
                                value
                            }
                            image: field(key: "image") {
                                value
                                reference {
                                    ... on MediaImage {
                                        image {
                                            url
                                        }
                                    }
                                }
                            }
                            color: field(key: "color") {
                                value
                            }
                            description: field(key: "description") {
                                value
                            }
                            fields {
                                key
                                value
                                reference {
                                    ... on MediaImage {
                                        image {
                                            url
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `);

            if (res.errors) {
                console.error(res.errors);
                break;
            }

            const nodes = res.data?.nodes?.filter(Boolean);
            if (Array.isArray(nodes)) {
                objects.push(...nodes);
            }
        }

        return objects;
    }

    async ensureFacetMetaObjectsLoaded (facets = this.facets) {
        const storefront = window.storefront;
        const idsToLoad = this.collectMetaobjectIdsFromFacets(facets).filter((id) => {
            const normalizedId = normalizeFilterLookupValue(id);
            return normalizedId
                && !this.filterObjectsMap.has(normalizedId)
                && !this.pendingFilterMetaIds.has(normalizedId);
        });

        if (!storefront || idsToLoad.length === 0) {
            return [];
        }

        idsToLoad.forEach((id) => {
            this.pendingFilterMetaIds.add(normalizeFilterLookupValue(id));
        });

        return this.fetchFilterMetaObjectsByIds(storefront.url, storefront.token, idsToLoad)
            .then((objects) => {
                if (objects.length > 0) {
                    this.mergeFilterObjects(objects);
                }

                if (this.widgetsInitialized && this.isDialogOpen()) {
                    this.refreshRenderedWidgets();
                }

                return objects;
            })
            .catch((error) => {
                console.error("Error loading PLP filter meta objects by id:", error);
                return [];
            })
            .finally(() => {
                idsToLoad.forEach((id) => {
                    this.pendingFilterMetaIds.delete(normalizeFilterLookupValue(id));
                });
            });
    }

    clearScheduledFilterMetaPrefetch () {
        if (this.filterMetaPrefetchId === null) return;

        if (typeof window.cancelIdleCallback === "function") {
            window.cancelIdleCallback(this.filterMetaPrefetchId);
        } else {
            window.clearTimeout(this.filterMetaPrefetchId);
        }

        this.filterMetaPrefetchId = null;
    }

    scheduleFilterMetaPrefetch () {
        if (this.filterMetaPromise || this.plpFiltersLoaded || this.filterMetaPrefetchId !== null) {
            return;
        }

        const loadMetaObjects = () => {
            this.filterMetaPrefetchId = null;
            this.ensureFilterMetaObjectsLoaded();
        };

        if (typeof window.requestIdleCallback === "function") {
            this.filterMetaPrefetchId = window.requestIdleCallback(loadMetaObjects, { timeout: 1500 });
        } else {
            this.filterMetaPrefetchId = window.setTimeout(loadMetaObjects, 1200);
        }
    }

    ensureFilterMetaObjectsLoaded () {
        if (this.filterMetaPromise) {
            return this.filterMetaPromise;
        }

        if (this.plpFiltersLoaded) {
            return Promise.resolve(this.filtersObjects);
        }

        const storefront = window.storefront;

        if (!storefront) {
            console.warn("Storefront object not found, cannot load filter meta object.");
            return Promise.resolve([]);
        }

        this.clearScheduledFilterMetaPrefetch();

        this.filterMetaPromise = this.fetchFiltersMetaObj(storefront.url, storefront.token)
            .then(objects => {
                this.mergeFilterObjects(objects);
                this.plpFiltersLoaded = true;

                if (this.widgetsInitialized && this.isDialogOpen()) {
                    this.refreshRenderedWidgets();
                }

                return this.filtersObjects;
            })
            .catch(err => {
                console.error("Error loading PLP filters meta object:", err);
                return [];
            })
            .finally(() => {
                this.filterMetaPromise = null;
            });

        return this.filterMetaPromise;
    }

    resolveRenderableFacets (facets = this.facets) {
        const facetsByCanonicalKey = new Map();
        const sourceFacets = Array.isArray(facets) ? facets : [];

        sourceFacets.forEach((facet) => {
            const canonicalKey = getFacetConfigKey(facet?.name, facet?.title);

            if (
                !canonicalKey
                || this.facetsBlacklist.includes(facet.name)
                || this.isStaticFilter(facet)
                || !this.isDisplayedFacet(facet.name)
            ) {
                return;
            }

            const currentFacet = facetsByCanonicalKey.get(canonicalKey);
            const currentFacetPreference = getFacetAliasPreferenceIndex(currentFacet?.name);
            const nextFacetPreference = getFacetAliasPreferenceIndex(facet.name);
            const currentFacetSize = Object.keys(currentFacet?.data || {}).length;
            const nextFacetSize = Object.keys(facet?.data || {}).length;

            if (
                !currentFacet
                || nextFacetPreference < currentFacetPreference
                || (nextFacetPreference === currentFacetPreference && nextFacetSize > currentFacetSize)
            ) {
                facetsByCanonicalKey.set(canonicalKey, facet);
            }
        });

        return this.sortFacetsByConfiguredOrder(Array.from(facetsByCanonicalKey.values()));
    }

    getRenderableFacets () {
        return this.resolveRenderableFacets(this.facets);
    }

    getRenderableFacetEntries (facets = this.facets) {
        const renderableFacets = this.resolveRenderableFacets(facets);
        const renderableFacetMap = new Map(
            renderableFacets.map((facet) => [getFacetConfigKey(facet?.name, facet?.title), facet])
        );
        const renderableFacetNameMap = new Map(
            renderableFacets.map((facet) => [normalizeFacetNameKey(facet?.name), facet])
        );
        const displayFacets = this.sortFacetsByConfiguredOrder(this.displayFacets || []);

        if (displayFacets.length === 0) {
            return renderableFacets
                .map((facet) => ({
                    key: getFacetConfigKey(facet?.name, facet?.title),
                    facet,
                }))
                .filter((entry) => entry.key && entry.facet);
        }

        return displayFacets
            .map((displayFacet) => {
                const facet = renderableFacetNameMap.get(normalizeFacetNameKey(displayFacet?.name))
                    || renderableFacetMap.get(getFacetConfigKey(displayFacet?.name, displayFacet?.title));

                if (!facet) {
                    return null;
                }

                return {
                    key: getFacetConfigKey(displayFacet?.name, displayFacet?.title),
                    facet,
                };
            })
            .filter((entry) => entry?.key && entry?.facet);
    }

    getFacetTitleFromConfig (facetName, fallbackTitle = "") {
        const normalizedFacetName = normalizeFacetNameKey(facetName);
        const canonicalKey = getFacetCanonicalKey(facetName);
        const fallbackCanonicalKey = FACET_TITLE_CANONICAL_MAP[normalizeFacetTitleKey(fallbackTitle)];

        return this.facetTitleMap.get(normalizedFacetName)
            || this.facetTitleMap.get(canonicalKey)
            || this.facetTitleMap.get(fallbackCanonicalKey)
            || "";
    }

    getFacetOrderIndex (facetName, fallbackTitle = "") {
        const normalizedFacetName = normalizeFacetNameKey(facetName);
        const canonicalKey = getFacetConfigKey(facetName, fallbackTitle);

        if (this.facetOrderMap.has(normalizedFacetName)) {
            return this.facetOrderMap.get(normalizedFacetName);
        }

        if (this.facetOrderMap.has(canonicalKey)) {
            return this.facetOrderMap.get(canonicalKey);
        }

        return Number.MAX_SAFE_INTEGER;
    }

    sortFacetsByConfiguredOrder (facets = []) {
        if (!Array.isArray(facets) || facets.length === 0) {
            return [];
        }

        const facetsByCanonicalKey = new Map();

        facets.forEach((facet) => {
            const canonicalKey = getFacetConfigKey(facet?.name, facet?.title);

            if (!canonicalKey) {
                return;
            }

            const currentFacet = facetsByCanonicalKey.get(canonicalKey);
            const currentFacetPreference = getFacetAliasPreferenceIndex(currentFacet?.name);
            const nextFacetPreference = getFacetAliasPreferenceIndex(facet?.name);

            if (!currentFacet || nextFacetPreference < currentFacetPreference) {
                facetsByCanonicalKey.set(canonicalKey, facet);
            }
        });

        if (this.facetOrderCanonicalKeys.length === 0) {
            return Array.from(facetsByCanonicalKey.values()).sort((left, right) => (
                String(left?.title || left?.name || "").localeCompare(String(right?.title || right?.name || ""))
            ));
        }

        const orderedFacets = this.facetOrderCanonicalKeys
            .map((canonicalKey) => facetsByCanonicalKey.get(canonicalKey))
            .filter(Boolean);

        const remainingFacets = Array.from(facetsByCanonicalKey.entries())
            .filter(([canonicalKey]) => !this.facetOrderCanonicalKeySet.has(canonicalKey))
            .map(([, facet]) => facet);

        return [...orderedFacets, ...remainingFacets];
    }

    renderFacetOptions (widget, facet, selectedValues = new Set()) {
        const accContent = widget?.querySelector("[data-acc-content]");
        const counterDisplay = widget?.querySelector("[data-acc-counter]");

        if (!accContent || !facet) return;

        const fragment = document.createDocumentFragment();
        const normalizedSelections = selectedValues instanceof Set ? selectedValues : new Set(selectedValues);
        let hasDescription = false;

        accContent.replaceChildren();
        accContent.toggleAttribute("data-has-description", false);

        Object.entries(facet.data || {}).forEach(([value, count]) => {
            const cbOpt = this.createCheckboxOption(this.checkboxOptionTemplate, facet.name, value, count);
            const checkbox = cbOpt.querySelector("input[type='checkbox']");

            if (checkbox) {
                checkbox.checked = normalizedSelections.has(value);
            }

            if (cbOpt.hasDescription) {
                hasDescription = true;
            }

            fragment.appendChild(cbOpt);
        });

        accContent.appendChild(fragment);
        accContent.toggleAttribute("data-has-description", hasDescription);
        widget.dataset.rendered = "true";
        this.updateCounterDisplay(counterDisplay, normalizedSelections.size);
    }

    createFacetWidget (facetEntry, selectedMap) {
        const facet = facetEntry?.facet || facetEntry;
        const facetName = facet.name;
        const facetKey = facetEntry?.key || getFacetConfigKey(facetName, facet?.title);
        const clone = this.multiChoiseTemplate.content.cloneNode(true);
        const widget = clone.querySelector("[data-facet]");
        const accTitle = clone.querySelector("[data-acc-title]");
        const details = clone.querySelector("details");
        const selectedValues = selectedMap.get(facetKey) || new Set();

        widget.dataset.facet = facetName;
        widget.dataset.facetKey = facetKey;
        widget.dataset.rendered = "false";

        if (accTitle) {
            accTitle.textContent = this.getFacetTitle(facetName);
        }

        if (details) {
            details.dataset.facet = facetName;
            details.dataset.facetKey = facetKey;
            details.addEventListener("toggle", this.widget_toggleHandler);

            if (selectedValues.size > 0) {
                details.open = true;
            }
        }

        this.updateCounterDisplay(widget.querySelector("[data-acc-counter]"), selectedValues.size);

        if (selectedValues.size > 0) {
            this.renderFacetOptions(widget, facet, selectedValues);
        }

        return widget;
    }

    refreshRenderedWidgets () {
        const facetMap = new Map(this.getRenderableFacetEntries().map(({ key, facet }) => [key, facet]));
        const selectedMap = this.captureDynamicFacetSelections();
        const activeWidgets = [];

        this.widgets.forEach((widget) => {
            const facetName = widget.dataset.facet;
            const facetKey = widget.dataset.facetKey || getFacetConfigKey(facetName);
            const facet = facetMap.get(facetKey);

            if (!facet) {
                this.widgetElementsByFacet.delete(facetKey);
                widget.remove();
                return;
            }

            activeWidgets.push(widget);
            widget.dataset.facet = facet.name;
            widget.dataset.facetKey = facetKey;
            const details = widget.querySelector("details");

            if (details) {
                details.dataset.facet = facet.name;
                details.dataset.facetKey = facetKey;
            }

            if (widget.dataset.rendered === "true") {
                this.renderFacetOptions(widget, facet, selectedMap.get(facetKey));
            } else {
                this.updateCounterDisplay(
                    widget.querySelector("[data-acc-counter]"),
                    selectedMap.get(facetKey)?.size || 0
                );
            }
        });

        this.widgets = activeWidgets;
    }

    needsWidgetRender () {
        const renderableFacetEntries = this.getRenderableFacetEntries();
        const widgetFacetNames = this.widgets.map((widget) => widget.dataset.facet);
        const widgetFacetKeys = this.widgets.map((widget) => widget.dataset.facetKey || getFacetConfigKey(widget.dataset.facet));

        if (renderableFacetEntries.length !== widgetFacetKeys.length) {
            return true;
        }

        return renderableFacetEntries.some(({ key, facet }, index) => (
            widgetFacetKeys[index] !== key || widgetFacetNames[index] !== facet.name
        ));
    }

    widget_toggleHandler = (event) => {
        const details = event.currentTarget;

        if (!details?.open) return;

        const facetName = details.dataset.facet;
        const facetKey = details.dataset.facetKey || getFacetConfigKey(facetName);
        const widget = facetKey ? this.widgetElementsByFacet.get(facetKey) : null;

        if (!widget || widget.dataset.rendered === "true") {
            return;
        }

        const facet = this.getRenderableFacetEntries()
            .find((entry) => entry.key === facetKey)?.facet;
        if (!facet) return;

        const selectedMap = this.captureDynamicFacetSelections();
        this.renderFacetOptions(widget, facet, selectedMap.get(facetKey));
        this.updateWidgets({ includeDynamicFacets: true });
    }

    handleDialogOpened = async () => {
        if (!this.dialog?.hasAttribute("open")) {
            return;
        }

        if (
            this.facets.length === 0
            || this.shouldBootstrapConfiguredFacets(this.facets)
            || this.hasMissingManagedFacets(this.facets)
        ) {
            const newFacets = await this.resolveInitialFacets(this.facets);
            if (newFacets.length > 0) {
                this.updateFacets(newFacets, true);
            }
        }

        await this.ensureFilterMetaObjectsLoaded();
        await this.ensureFacetMetaObjectsLoaded(this.facets);

        if (!this.widgetsInitialized || this.pendingWidgetRefresh || this.needsWidgetRender()) {
            this.renderWidgets();
        } else {
            this.updateWidgets({ includeDynamicFacets: true });
        }
    }

    getAlgoliaHelper () {
        return window.algoliaShopify?.instantsearch?.search?.helper || null;
    }

    syncOriginalSortWidget (indexName) {
        const originalSort = document.querySelector(".po-original-sort .ais-SortBy-select");

        if (!originalSort || !indexName) {
            return;
        }

        if (originalSort.value !== indexName) {
            originalSort.value = indexName;
        }
    }

    syncAlgoliaState (indexName, facetsFilters = []) {
        const resolvedIndex = normalizeQueryIndexName(
            indexName || this.lastIndex || window.__poPlpCurrentIndexName || null
        );
        const helper = this.getAlgoliaHelper();
        const normalizedFacetFilters = Array.isArray(facetsFilters) ? facetsFilters : [];

        if (resolvedIndex) {
            window.__poPlpCurrentIndexName = resolvedIndex;
            if (window.algoliaPoCustomSearch) {
                window.algoliaPoCustomSearch.loadMoreIndex = resolvedIndex;
            }
            this.syncOriginalSortWidget(resolvedIndex);
        }

        if (!helper) {
            return;
        }

        try {
            if (resolvedIndex && helper.state?.index !== resolvedIndex) {
                if (typeof helper.setIndex === "function") {
                    helper.setIndex(resolvedIndex);
                } else if (helper.state) {
                    helper.state.index = resolvedIndex;
                }
            }

            if (typeof helper.setQueryParameter === "function") {
                helper.setQueryParameter(
                    "facetFilters",
                    normalizedFacetFilters.length > 0 ? normalizedFacetFilters : undefined
                );
                helper.setPage?.(0);
            } else if (helper.state) {
                helper.state.facetFilters = normalizedFacetFilters.length > 0 ? normalizedFacetFilters : undefined;
                if (typeof helper.state.page === "number") {
                    helper.state.page = 0;
                }
            }
        } catch (error) {
            console.warn("Unable to sync Algolia helper state", error);
        }
    }

    buildTealiumFacetsFromFormData (formData) {
        const tealiumFacets = [];
        const uniqueKeys = new Set();

        for (const key of formData.keys()) {
            if (uniqueKeys.has(key)) {
                continue;
            }

            uniqueKeys.add(key);
            const choices = formData.getAll(key);

            if (choices.length > 0 && !key.startsWith("_") && key !== "sort_by") {
                tealiumFacets.push(
                    choices.map((entry) => {
                        const checkbox = this.form?.querySelector(
                            `input[type='checkbox'][name='${key}'][value='${entry}']`
                        );
                        const formattedKey = key.replace("meta.custom.", "");
                        return `${formattedKey}:${checkbox?.dataset?.tealiumValue || entry}`;
                    })
                );
            }
        }

        return tealiumFacets;
    }

    dispatchFiltersChanged (facetsFilters = [], index, showResults, tealiumFacets = [], sendTealiumData = false) {
        const joinedFilters = facetsFilters.length > 0 ? facetsFilters.join(",") : null;
        const resolvedIndex = normalizeQueryIndexName(
            index
            || this.lastIndex
            || window.poAlgoliaUtils?.getLastIndexName?.()
            || window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index
        );
        const selFacets = (this.facets || []).map(f => f.name);

        if (this.lastFilters !== joinedFilters || this.lastIndex !== resolvedIndex || showResults) {
            this.syncAlgoliaState(resolvedIndex, facetsFilters);
            poPlpFiltersSortDebug("dispatchFiltersChanged", {
                resolvedIndex,
                showResults,
                facetsFilters,
                selectedFacetNames: selFacets,
                previousIndex: this.lastIndex,
                previousFilters: this.lastFilters,
            });
            document.dispatchEvent(new CustomEvent("plp:filters-changed", {
                detail: { selFacets, facetsFilters, index: resolvedIndex, showResults, tealiumFacets, sendTealiumData }
            }));

            this.lastFilters = joinedFilters;
            this.lastIndex = resolvedIndex;
        }

        this.updateFilterActiveCount(facetsFilters);
    }

    updateFilterActiveCount (facetsFilters = []) {
        let count = 0;
        facetsFilters.forEach((entry) => {
            count += Array.isArray(entry) ? entry.length : (entry ? 1 : 0);
        });

        document.querySelectorAll("#filter-active").forEach((el) => {
            el.textContent = count > 0 ? ` (${count})` : "";
        });
    }

    async fetchFiltersMetaObj (url, token) {
        const country = window.Shopify?.country?.toUpperCase() || "US";
        const language = (window.Shopify?.locale || "EN").split("-")[0].toUpperCase();
        const objects = [];
        let hasNextPage = true;
        let cursor;

        while (hasNextPage) {
            const res = await Fetchutils.postGraphql(url, token, `#graphql
                query loadPlpFilters @inContext(country: ${country}, language: ${language}) {
                    metaobjects (type: "plp_filters", first: 250, after: ${cursor ? `"${cursor}"` : "null"}) {
                        nodes {
                            id
                            handle
                            label: field(key: "label") {
                                value
                            }
                            name: field(key: "name") {
                                value
                            }
                            title: field(key: "title") {
                                value
                            }
                            image: field(key: "image") {
                                value
                                reference {
                                    ... on MediaImage {
                                        image {
                                            url
                                        }
                                    }
                                }
                            }
                            color: field(key: "color") {
                                value
                            }
                            description: field(key: "description") {
                                value
                            }
                            fields {
                                key
                                value
                                reference {
                                    ... on MediaImage {
                                        image {
                                            url
                                        }
                                    }
                                }
                            }
                        }
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                    }
                }
            `);

            const nodes = res.data?.metaobjects?.nodes;

            if (Array.isArray(nodes)) {
                objects.push(...nodes);
            }

            if (res.errors) {
                console.error(res.errors);
                break;
            }

            hasNextPage = res.data?.metaobjects?.pageInfo?.hasNextPage;
            cursor = res.data?.metaobjects?.pageInfo?.endCursor;
        }

        return objects;
    }

    form_changedHandler = (event) => {
        const fieldset = event.target.closest("fieldset");
        const checkboxes = fieldset?.querySelectorAll("input[type='checkbox']");
        const counterDisplay = fieldset?.querySelector("[data-acc-counter]");
        const isSortChange = event.target?.name === "sort_by";

        let checkedCount = 0;

        checkboxes?.forEach(checkbox => {
            if (checkbox.checked) {
                checkedCount++;
            }
        });

        this.updateCounterDisplay(counterDisplay, checkedCount);

        if (isSortChange) {
            poPlpFiltersSortDebug("sort radio changed", {
                nextValue: event.target?.value || null,
                nextLabel: event.target?.closest("label")?.innerText?.trim() || "",
                lastIndex: this.lastIndex,
            });
            return;
        }

        window.clearTimeout(this.liveSubmitTimer);
        this.liveSubmitTimer = window.setTimeout(() => {
            this.submitFilters(false);
        }, 80);
    }

    dialogDismissTrigger_clickHandler = () => {
        this.setDialogCloseAction("dismiss");
    }

    dialog_clickCaptureHandler = (event) => {
        const isBackdropClick = event.target === this.dialog;
        const backdropClickClose = this.dialog?.getAttribute("data-backdrop-close") === "true";

        if (isBackdropClick && backdropClickClose) {
            this.setDialogCloseAction("dismiss");
        }
    }

    dialog_cancelHandler = () => {
        this.setDialogCloseAction("dismiss");
    }

    dialog_closeHandler = () => {
        const closeAction = this.dialogCloseAction || "dismiss";
        this.dialogCloseAction = null;

        if (closeAction === "apply" || closeAction === "reset") {
            return;
        }

        this.restoreAppliedState({
            syncPreview: this.hasDraftChangesComparedToApplied(),
        });
    }

    getSelectedSortValue () {
        return normalizeQueryIndexName(
            this.form?.querySelector("input[name='sort_by']:checked")?.value
            || this.lastIndex
            || window.poAlgoliaUtils?.getLastIndexName?.()
            || window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index
            || ""
        );
    }

    getDefaultSortValue () {
        const sortInputs = Array.from(this.form?.querySelectorAll("input[name='sort_by']") || []);
        const defaultProductsIndex =
            window.algoliaPoCustomSearch?.getDefaultProductsIndex?.()
            || window.poAlgoliaUtils?.getDefaultProductsIndex?.()
            || "";

        const defaultInput =
            sortInputs.find((input) => normalizeSortValue(input.value) === normalizeSortValue(defaultProductsIndex))
            || sortInputs.find((input) => input.dataset.optLabel === "relevance")
            || sortInputs.find((input) => isBaseProductsIndexValue(input.value))
            || sortInputs[0];

        return defaultInput?.value || defaultProductsIndex || "";
    }

    resetFilters ({ showResults = true, closeDialog = true } = {}) {
        if (!this.form) return;

        window.clearTimeout(this.liveSubmitTimer);

        const resetSortValue = this.getDefaultSortValue();

        this.form.querySelectorAll("input[type='checkbox']").forEach((input) => {
            input.checked = false;
            input.defaultChecked = false;
        });

        this.form.querySelectorAll("input[name='sort_by']").forEach((input) => {
            const isSelected = Boolean(resetSortValue) && input.value === resetSortValue;
            input.checked = isSelected;
            input.defaultChecked = isSelected;
        });

        this.form.querySelectorAll("details[data-facet]").forEach((details) => {
            details.open = false;
        });

        this.form.querySelectorAll("[data-acc-counter]").forEach((counter) => {
            this.updateCounterDisplay(counter, 0);
        });

        if (this.widgetsInitialized) {
            this.refreshRenderedWidgets();
        } else {
            this.updateWidgets({ includeDynamicFacets: true });
        }

        if (showResults) {
            this.commitAppliedState([], resetSortValue || null);
        }

        this.dispatchFiltersChanged([], resetSortValue || null, showResults);

        if (closeDialog) {
            this.setDialogCloseAction(showResults ? "reset" : null);
            this.dialog?.close();
        }
    }

    form_resetHandler = (event) => {
        event?.preventDefault?.();
        this.resetFilters({ showResults: true, closeDialog: true });
    }

    form_submitHandler = (event) => {
        window.clearTimeout(this.liveSubmitTimer);
        this.setDialogCloseAction("apply");
        this.submitFilters(true, true);
        event.preventDefault();
        this.dialog?.close();
    }

    resetButton_clickHandler = (event) => {
        event.preventDefault();
        this.resetFilters({ showResults: true, closeDialog: true });
    }

    submitFilters (showResults, sendTealiumData = false) {
        const formData = new FormData(this.form);
        const facetsFilters = this.buildFacetFiltersFromFormData(formData);
        const tealiumFacets = this.buildTealiumFacetsFromFormData(formData);
        const sortBy = formData.get("sort_by")
            || this.lastIndex
            || window.poAlgoliaUtils?.getLastIndexName?.()
            || window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index;

        poPlpFiltersSortDebug("submitFilters", {
            showResults,
            sortBy,
            facetsFilters,
            lastIndex: this.lastIndex,
        });

        if (showResults) {
            this.commitAppliedState(facetsFilters, sortBy);
        }

        this.dispatchFiltersChanged(facetsFilters, sortBy, showResults, tealiumFacets, sendTealiumData);
    }

    getFacetTitle (facetName) {
        const facetKey = getFacetConfigKey(facetName);
        const displayFacet = this.displayFacets.find((df) => (
            getFacetConfigKey(df.name, df.title) === facetKey
        ));

        return this.getFacetTitleFromConfig(facetName, displayFacet?.title)
            || displayFacet?.title
            || facetName;
    }

    isStaticFilter (facet) {
        return facet.data && Object.keys(facet.data).length < 3 && this.filterBy?.elements.namedItem(facet.name);
    }

    isDisplayedFacet (facetName) {
        const targetCanonicalKey = getFacetConfigKey(facetName);

        return !this.config.onlyDisplayFacets || this.displayFacets.some((displayFacet) => (
            displayFacet.enabled
            && displayFacet.type !== "hidden"
            && getFacetConfigKey(displayFacet.name, displayFacet.title) === targetCanonicalKey
        ));
    }

    loadFilterMetaObj () {
        return this.ensureFilterMetaObjectsLoaded();
    }

    getStoredBaseFacets () {
        try {
            const storedFacets = JSON.parse(sessionStorage.getItem("plpFacets") || "null");
            return Array.isArray(storedFacets) ? storedFacets : null;
        } catch (_error) {
            return null;
        }
    }

    getCurrentFacetFilters () {
        const rawFacetFilters = window.algoliaShopify?.instantsearch?.search?.helper?.state?.facetFilters;

        if (Array.isArray(rawFacetFilters)) {
            return rawFacetFilters;
        }

        if (typeof rawFacetFilters === "string") {
            try {
                const parsed = JSON.parse(rawFacetFilters);
                return Array.isArray(parsed) ? parsed : [];
            } catch (_error) {
                return [];
            }
        }

        return [];
    }

    hasActiveFacetFilters () {
        return this.getCurrentFacetFilters().some((entry) => (
            Array.isArray(entry) ? entry.some(Boolean) : Boolean(entry)
        ));
    }

    getAlgoliaFacetsWidgetSource () {
        return window.algoliaShopify?.facetsWidgets || [];
    }

    normalizeConfiguredFacetEntry (entry, key = "") {
        if (!entry) {
            return null;
        }

        if (typeof entry === "string") {
            return {
                name: key || entry,
                title: key ? entry : this.getFacetTitleFromConfig(entry),
                enabled: true,
                type: "widget",
            };
        }

        if (typeof entry !== "object") {
            return null;
        }

        const facetConfig = entry?.facet && typeof entry.facet === "object"
            ? entry.facet
            : null;
        const widgetParams = entry?.params && typeof entry.params === "object"
            ? entry.params
            : null;
        const getFirstString = (...values) =>
            values.flat(Infinity).find((value) => typeof value === "string" && value.trim());
        const directEntryName = !facetConfig && !widgetParams
            ? getFirstString(
                entry.name,
                entry.attribute,
                entry.attributeName,
                entry.facet,
                entry.field
            )
            : "";

        const name =
            getFirstString(
                facetConfig?.name,
                widgetParams?.attribute,
                widgetParams?.attributeName,
                widgetParams?.attributes,
                entry.attribute,
                entry.attributeName,
                entry.field,
                entry.renderState?.attribute,
                entry.renderState?.attributeName,
                entry.renderingContent?.attribute,
                directEntryName,
                isUsableFacetFallbackKey(key) ? key : ""
            );

        if (!name) {
            return null;
        }

        return {
            ...entry,
            name,
            title:
                facetConfig?.title ||
                facetConfig?.label ||
                entry.title ||
                entry.label ||
                entry.displayName ||
                entry.display_name ||
                widgetParams?.title ||
                widgetParams?.label ||
                this.getFacetTitleFromConfig(name, facetConfig?.title || ""),
            enabled: facetConfig?.enabled !== false && entry.enabled !== false && entry.hidden !== true,
            type: facetConfig?.type || entry.type || entry.widgetType || "widget",
        };
    }

    normalizeConfiguredFacetCollection (collection, seen = new WeakSet(), depth = 0) {
        if (!collection || depth > 8) {
            return [];
        }

        if (Array.isArray(collection)) {
            return collection
                .flatMap((entry) => this.normalizeConfiguredFacetCollection(entry, seen, depth + 1))
                .filter(Boolean);
        }

        if (typeof collection !== "object") {
            return [];
        }

        if (seen.has(collection)) {
            return [];
        }

        seen.add(collection);

        const normalizedEntry = this.normalizeConfiguredFacetEntry(collection);
        const nestedCollections = [
            collection.items,
            collection.facets,
            collection.widgets,
            collection.attributes,
            collection.results,
            collection.widgetParams,
            collection.params,
            collection.renderState,
            collection.state,
            collection.props,
            collection.options,
            collection.config,
            collection.children,
            collection.__widgets,
            collection._widgets,
            ...Object.values(collection),
        ].filter(Boolean);

        const nestedEntries = nestedCollections.flatMap((entry) =>
            this.normalizeConfiguredFacetCollection(entry, seen, depth + 1)
        );
        const combinedEntries = normalizedEntry ? [normalizedEntry, ...nestedEntries] : nestedEntries;
        const dedupedEntries = new Map();

        combinedEntries.forEach((entry) => {
            const configKey = getFacetConfigKey(entry?.name, entry?.title);

            if (configKey && !dedupedEntries.has(configKey)) {
                dedupedEntries.set(configKey, entry);
            }
        });

        return Array.from(dedupedEntries.values());
    }

    getConfiguredDisplayFacets (activeCurrency = getActiveCurrency() || this.activeCurrency) {
        const facetsWidget = this.getAlgoliaFacetsWidgetSource();
        const widgetDisplayFacets = filterFacetsByCurrency(
            this.normalizeConfiguredFacetCollection(facetsWidget),
            activeCurrency
        );

        if (!Array.isArray(widgetDisplayFacets)) {
            return [];
        }

        const facetsByCanonicalKey = new Map();

        widgetDisplayFacets.forEach((facet) => {
            const canonicalKey = getFacetConfigKey(facet?.name, facet?.title);

            if (
                !facet?.enabled
                || facet?.type === "hidden"
                || !canonicalKey
                || (this.facetOrderCanonicalKeySet.size > 0 && !this.facetOrderCanonicalKeySet.has(canonicalKey))
            ) {
                return;
            }

            const currentFacet = facetsByCanonicalKey.get(canonicalKey);
            const currentFacetPreference = getFacetAliasPreferenceIndex(currentFacet?.name);
            const nextFacetPreference = getFacetAliasPreferenceIndex(facet?.name);

            if (!currentFacet || nextFacetPreference < currentFacetPreference) {
                facetsByCanonicalKey.set(canonicalKey, facet);
            }
        });

        return this.sortFacetsByConfiguredOrder(Array.from(facetsByCanonicalKey.values()));
    }

    getConfiguredDisplayFacetNames (activeCurrency = getActiveCurrency() || this.activeCurrency) {
        return this.getConfiguredDisplayFacets(activeCurrency)
            .map((facet) => facet?.name)
            .filter(Boolean);
    }

    getFacetCanonicalKeySet (facets = []) {
        return new Set(
            this.normalizeFacetEntries(facets)
                .map((facet) => getFacetConfigKey(facet?.name, facet?.title))
                .filter(Boolean)
        );
    }

    hasMissingManagedFacets (facets = []) {
        const configuredDisplayFacets = this.getConfiguredDisplayFacets();

        if (configuredDisplayFacets.length === 0) {
            return false;
        }

        const availableCanonicalKeys = new Set(
            this.getResolvedDisplayFacets(facets)
                .map((facet) => getFacetConfigKey(facet?.name, facet?.title))
                .filter(Boolean)
        );
        const requiredCanonicalKeys = Array.from(
            new Set(
                configuredDisplayFacets
                    .map((facet) => getFacetConfigKey(facet?.name, facet?.title))
                    .filter(Boolean)
            )
        );

        return requiredCanonicalKeys.some((canonicalKey) => !availableCanonicalKeys.has(canonicalKey));
    }

    mergeFacetEntries (...facetGroups) {
        const facetsByCanonicalKey = new Map();
        const sourceFacets = facetGroups.flatMap((facetGroup) => this.normalizeFacetEntries(facetGroup));

        sourceFacets.forEach((facet) => {
            const canonicalKey = getRenderableFacetKey(facet?.name);

            if (!canonicalKey) {
                return;
            }

            const currentFacet = facetsByCanonicalKey.get(canonicalKey);
            const currentFacetPreference = getFacetAliasPreferenceIndex(currentFacet?.name);
            const nextFacetPreference = getFacetAliasPreferenceIndex(facet?.name);
            const currentFacetSize = Object.keys(currentFacet?.data || {}).length;
            const nextFacetSize = Object.keys(facet?.data || {}).length;

            if (
                !currentFacet
                || nextFacetPreference < currentFacetPreference
                || (nextFacetPreference === currentFacetPreference && nextFacetSize > currentFacetSize)
            ) {
                facetsByCanonicalKey.set(canonicalKey, {
                    name: facet.name,
                    data: facet?.data && typeof facet.data === "object" ? { ...facet.data } : {},
                });
            }
        });

        return Array.from(facetsByCanonicalKey.values());
    }

    buildFallbackDisplayFacets (facets = []) {
        if (!Array.isArray(facets)) {
            return [];
        }

        return this.sortFacetsByConfiguredOrder(facets)
            .map((facet) => {
                const name = facet?.name;

                if (!name) {
                    return null;
                }

                return {
                    name,
                    enabled: true,
                    type: "fallback",
                    title: this.getFacetTitleFromConfig(name, facet?.title),
                };
            })
            .filter(Boolean);
    }

    getResolvedDisplayFacets (facets = [], activeCurrency = getActiveCurrency() || this.activeCurrency) {
        const configuredDisplayFacets = this.getConfiguredDisplayFacets(activeCurrency);
        const sourceFacets = Array.isArray(facets) && facets.length > 0
            ? facets
            : this.facets;
        const normalizedSourceFacets = this.normalizeFacetEntries(sourceFacets);
        const availableFacetsByName = new Map(
            normalizedSourceFacets
                .map((facet) => [normalizeFacetNameKey(facet?.name), facet])
                .filter(([name, facet]) => Boolean(name) && Boolean(facet))
        );
        const availableFacetNames = new Set(
            normalizedSourceFacets
                .map((facet) => facet?.name)
                .filter(Boolean)
        );
        const availableFacetsByCanonicalKey = new Map();

        normalizedSourceFacets.forEach((facet) => {
            const canonicalKey = getFacetConfigKey(facet?.name, facet?.title);
            const currentFacet = availableFacetsByCanonicalKey.get(canonicalKey);
            const currentFacetPreference = getFacetAliasPreferenceIndex(currentFacet?.name);
            const nextFacetPreference = getFacetAliasPreferenceIndex(facet?.name);
            const currentFacetSize = Object.keys(currentFacet?.data || {}).length;
            const nextFacetSize = Object.keys(facet?.data || {}).length;

            if (
                !currentFacet
                || nextFacetPreference < currentFacetPreference
                || (nextFacetPreference === currentFacetPreference && nextFacetSize > currentFacetSize)
            ) {
                availableFacetsByCanonicalKey.set(canonicalKey, facet);
            }
        });

        if (configuredDisplayFacets.length > 0) {
            if (availableFacetNames.size === 0) {
                return this.sortFacetsByConfiguredOrder(configuredDisplayFacets);
            }

            const matchingConfiguredFacets = configuredDisplayFacets
                .map((facet) => {
                    const exactFacet = availableFacetsByName.get(normalizeFacetNameKey(facet?.name));
                    const canonicalKey = getFacetConfigKey(facet?.name, facet?.title);
                    const availableFacet = exactFacet || availableFacetsByCanonicalKey.get(canonicalKey);

                    if (!availableFacet) {
                        return null;
                    }

                    return {
                        ...facet,
                        name: availableFacet.name,
                        title: this.getFacetTitleFromConfig(availableFacet.name, facet.title) || facet.title,
                    };
                })
                .filter(Boolean);

            if (matchingConfiguredFacets.length > 0) {
                return this.sortFacetsByConfiguredOrder(matchingConfiguredFacets);
            }
        }

        return [];
    }

    normalizeFacetEntries (facets) {
        if (Array.isArray(facets)) {
            return facets
                .map((facet) => {
                    if (!facet?.name) {
                        return null;
                    }

                    return {
                        name: facet.name,
                        data: facet?.data && typeof facet.data === "object" ? { ...facet.data } : {},
                    };
                })
                .filter(Boolean);
        }

        if (facets && typeof facets === "object") {
            return Object.entries(facets).map(([name, data]) => ({
                name,
                data: data && typeof data === "object" ? { ...data } : {},
            }));
        }

        return [];
    }

    shouldBootstrapConfiguredFacets (facets = []) {
        const configuredFacetCanonicalKeys = Array.from(
            new Set(
                this.getConfiguredDisplayFacetNames()
                    .map((name) => getFacetConfigKey(name))
                    .filter(Boolean)
            )
        );

        if (configuredFacetCanonicalKeys.length === 0) {
            return false;
        }

        const currentFacetCanonicalKeys = this.getFacetCanonicalKeySet(facets);
        const hasConfiguredMatch = configuredFacetCanonicalKeys.some((name) => currentFacetCanonicalKeys.has(name));

        if (currentFacetCanonicalKeys.size > 0 && !hasConfiguredMatch) {
            return false;
        }

        const matchedConfiguredFacetCount = configuredFacetCanonicalKeys.filter((name) => currentFacetCanonicalKeys.has(name)).length;
        const minimumExpectedMatches = Math.min(3, configuredFacetCanonicalKeys.length);

        return matchedConfiguredFacetCount < minimumExpectedMatches;
    }

    async fetchFacetEntries (facetNames = ["*"]) {
        const queryUrl = window.poAlgoliaUtils?.buildQueryUrl?.();
        const baseParams =
            window.algoliaPoCustomSearch?.fetchParams ||
            window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.params;
        const indexName =
            window.poAlgoliaUtils?.getLastIndexName?.(null) ||
            window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index;
        const requestedFacetNames = Array.isArray(facetNames) && facetNames.length > 0
            ? facetNames
            : ["*"];

        if (!queryUrl || !baseParams || requestedFacetNames.length === 0 || !indexName) {
            return [];
        }

        return fetch(queryUrl.toString(), {
            method: "POST",
            body: JSON.stringify({
                requests: [{
                    indexName,
                    params: (() => {
                        const params = new URLSearchParams(baseParams);
                        params.set("hitsPerPage", "0");
                        params.set("facets", JSON.stringify(requestedFacetNames));
                        return params.toString();
                    })(),
                }],
            }),
        })
            .then((response) => response.json())
            .then((data) => this.normalizeFacetEntries(data?.results?.[0]?.facets));
    }

    async fetchConfiguredFacets () {
        if (this.configuredFacetsPromise) {
            return this.configuredFacetsPromise;
        }

        const facetNames = this.getConfiguredDisplayFacetNames();

        if (facetNames.length === 0) {
            return [];
        }

        this.configuredFacetsPromise = this.fetchFacetEntries(facetNames)
            .catch((error) => {
                console.error("Error fetching configured PLP facets:", error);
                return [];
            })
            .finally(() => {
                this.configuredFacetsPromise = null;
            });

        return this.configuredFacetsPromise;
    }

    async fetchAvailableFacets () {
        if (this.availableFacetsPromise) {
            return this.availableFacetsPromise;
        }

        this.availableFacetsPromise = this.fetchFacetEntries(["*"])
            .catch((error) => {
                console.error("Error fetching available PLP facets:", error);
                return [];
            })
            .finally(() => {
                this.availableFacetsPromise = null;
            });

        return this.availableFacetsPromise;
    }

    async resolveInitialFacets (facets) {
        let nextFacets = this.normalizeFacetEntries(Array.isArray(facets) ? facets : []);

        if (nextFacets.length < 3) {
            nextFacets = this.mergeFacetEntries(nextFacets, this._getRawFacets());
        }

        if (this.shouldBootstrapConfiguredFacets(nextFacets)) {
            const configuredFacets = await this.fetchConfiguredFacets();

            if (configuredFacets.length > 0) {
                nextFacets = this.mergeFacetEntries(nextFacets, configuredFacets);
            }
        }

        if (nextFacets.length === 0 || this.hasMissingManagedFacets(nextFacets)) {
            const availableFacets = await this.fetchAvailableFacets();

            if (availableFacets.length > 0) {
                nextFacets = this.mergeFacetEntries(nextFacets, availableFacets);
            }
        }

        return nextFacets;
    }

    getResultFacetFilters (result) {
        const rawFacetFilters = new URLSearchParams(result?.params || "").get("facetFilters");

        if (!rawFacetFilters) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawFacetFilters);
            return Array.isArray(parsed) ? parsed : [];
        } catch (_error) {
            return [];
        }
    }

    resultIncludesFacetFilter (result, facetName) {
        if (!facetName) return false;

        return this.getResultFacetFilters(result).some((entry) => {
            if (Array.isArray(entry)) {
                return entry.some((value) => String(value || "").startsWith(`${facetName}:`));
            }

            return String(entry || "").startsWith(`${facetName}:`);
        });
    }

    plpFacetsLoaded_handler = async (event) => {
        const newFacets = await this.resolveInitialFacets(event.detail.facets);

        if (this.facets.length === 0 || !this.lastFilters || this.hasMissingManagedFacets(this.facets)) {
            this.updateFacets(newFacets, false);
            this.maxHits = event.detail.maxHits || 0;
        }

        const currentHelperState = this.buildFilterState({
            facetFilters: this.getCurrentFacetFilters(),
            sortBy: this.getCurrentAppliedSortValue(),
        });

        if (
            !this.initialUrlStateApplied
            && this.hasInitialUrlState()
            && currentHelperState.facetFilters.length === 0
        ) {
            this.initialUrlStateApplied = true;
            this.applyStateToForm(this.initialUrlState);
            this.commitAppliedState(this.initialUrlState.facetFilters, this.initialUrlState.sortBy);
            this.dispatchFiltersChanged(
                this.initialUrlState.facetFilters,
                this.initialUrlState.sortBy || null,
                true
            );
            return;
        }

        this.commitAppliedState(this.getCurrentFacetFilters(), this.getCurrentAppliedSortValue());
    }

    plpFacetsUpdated_handler = (event) => {
        const savedFacets = this.getStoredBaseFacets();
        const activeCurrency = getActiveCurrency();
        const initialFacets = filterFacetsByCurrency(
            Array.isArray(savedFacets) ? savedFacets : this.facets,
            activeCurrency
        ).map((facet) => ({
            ...facet,
            data: facet?.data && typeof facet.data === "object" ? { ...facet.data } : {},
        }));
        const results = event.detail.results || [];
        const newFacets = [];

        for (let facet of initialFacets) {
            const facetData = facet.data;
            let hasFacetSnapshot = false;

            for (let result of results) {
                if (this.resultIncludesFacetFilter(result, facet.name)) {
                    continue;
                }

                if (!result.facets) {
                    console.warn("No facets in result for:", result?.params);
                    continue;
                }

                const resultFacetData = result.facets[facet.name];

                if (!resultFacetData || typeof resultFacetData !== "object") {
                    continue;
                }

                hasFacetSnapshot = true;

                for (let value of [...Object.keys(facetData)]) {
                    const count = resultFacetData[value];

                    if (!count) {
                        delete facetData[value];
                        continue;
                    }

                    if (count < facetData[value]) {
                        facetData[value] = count;
                    }
                }
            }

            if (!hasFacetSnapshot || Object.keys(facetData).length > 0) {
                newFacets.push(facet);
            }
        }

        this.updateFacets(newFacets, true);
    }

    plpFacetsCountUpdated_handler = (event) => {
        this.maxHits = Math.max(0, Number(event?.detail?.nbHits || 0));
        this.updateResultHitsCount(this.maxHits);
    }

    sortOrdersUpdated_handler = () => {
        this.updateSortByOptions();
    }

    plpResetFilters_handler = () => {
        this.resetFilters({ showResults: true, closeDialog: false });
    }

    plpOpenFilters_handler = () => {
        this.restoreAppliedState({ syncPreview: false });
        this.dialog?.showModal();
    }

    renderWidgets () {
        if (!this.form) {
            console.error("Form container not found for PLP filters.");
            return;
        }

        if (!this.multiChoiseTemplate) {
            console.error("Multi-choice template not found for PLP filters.");
            return;
        }

        if (!this.checkboxOptionTemplate) {
            console.error("Checkbox option template not found for PLP filters.");
            return;
        }

        this.updateSortByOptions();

        this.widgets.forEach(w => w.remove());
        this.widgets.length = 0;
        this.widgetElementsByFacet.clear();

        const selectedMap = this.captureDynamicFacetSelections();
        const fragment = document.createDocumentFragment();

        for (let facetEntry of this.getRenderableFacetEntries()) {
            const widget = this.createFacetWidget(facetEntry, selectedMap);
            this.widgets.push(widget);
            this.widgetElementsByFacet.set(facetEntry.key, widget);
            fragment.appendChild(widget);
        }

        this.form.appendChild(fragment);
        this.widgetsInitialized = true;
        this.pendingWidgetRefresh = false;
        this.updateWidgets({ includeDynamicFacets: true });
        this.updateResultHitsCount(this.maxHits);
    }

    updateCounterDisplay (display, count) {
        if (display) {
            display.textContent = count > 0 ? `(${count})` : '';
            display.dataset.accCounter = count;
            display.toggleAttribute("data-hidden", count === 0);
        }
    }

    updateFacets (newFacets, preventRender) {
        const activeCurrency = getActiveCurrency() || this.activeCurrency;
        const filteredFacets = filterFacetsByCurrency(newFacets, activeCurrency);
        const resolvedDisplayFacets = this.getResolvedDisplayFacets(filteredFacets, activeCurrency);

        if (Array.isArray(filteredFacets) && Array.isArray(resolvedDisplayFacets)) {
            this.facets = filteredFacets;
            this.displayFacets = resolvedDisplayFacets;

            if (!this.lastIndex) {
                this.lastIndex = window.poAlgoliaUtils?.getLastIndexName?.() || window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index;
            }

            if (!this.lastFilters && !this.hasActiveFacetFilters()) {
                sessionStorage.setItem("plpFacets", JSON.stringify(this.facets));
            }

            this.ensureFacetMetaObjectsLoaded(this.facets);

            const shouldRebuildWidgets = this.needsWidgetRender() || (!this.lastFilters && !preventRender);

            if (!this.widgetsInitialized) {
                this.pendingWidgetRefresh = true;
                this.updateWidgets({ includeDynamicFacets: false });
            } else if (!this.isDialogOpen()) {
                this.pendingWidgetRefresh = true;
                this.updateWidgets({ includeDynamicFacets: false });
            } else if (shouldRebuildWidgets) {
                this.renderWidgets();
            } else {
                this.pendingWidgetRefresh = false;
                this.updateWidgets({ includeDynamicFacets: true });
            }
        }
    }

    updateWidgets (options = {}) {
        if (!this.form) return;

        const includeDynamicFacets = options.includeDynamicFacets ?? this.isDialogOpen();
        const facetMap = new Map(this.facets.map((facet) => [facet.name, facet]));
        const renderableFacetEntries = this.getRenderableFacetEntries();
        const renderableFacetMap = new Map(renderableFacetEntries.map(({ key, facet }) => [key, facet]));
        const displayedFacetKeys = new Set(renderableFacetEntries.map(({ key }) => key));

        for (let element of this.form.elements) {
            const facet = facetMap.get(element.name);
            const facetData = facet?.data;

            if (facetData && this.filterBy?.contains(element)) {
                if (element.dataset.dynamicValue !== undefined) {
                    const availableKeys = Object.keys(facetData);
                    if (availableKeys.length > 0 && !availableKeys.some(k => k.toLowerCase() === element.value.toLowerCase())) {
                        element.value = availableKeys[0];
                    }
                }

                if (Object.keys(facetData).some(key => key.toLowerCase() === element.value.toLowerCase())) {
                    element.disabled = false;
                } else {
                    element.disabled = true;
                    element.checked = false;
                }

                continue;
            }

            if (!includeDynamicFacets) {
                continue;
            }

            const facetKey = element.closest("[data-facet]")?.dataset.facetKey || getFacetConfigKey(element.name);
            const renderableFacet = renderableFacetMap.get(facetKey);
            const renderableFacetData = renderableFacet?.data;

            if (displayedFacetKeys.has(facetKey) && element.dataset.count) {
                if (renderableFacetData && renderableFacetData[element.value]) {
                    element.dataset.count = renderableFacetData[element.value];
                    element.disabled = false;
                } else {
                    element.dataset.count = 0;
                    element.disabled = true;
                    element.checked = false;
                }

            }
        }
    }

    updateResultHitsCount (count) {
        if (this.resultCountDisplay) {
            this.resultCountDisplay.textContent = `(${count})`;
            this.resultCountDisplay.dataset.resultCount = count;
            this.resultCountDisplay.toggleAttribute("data-hidden", !count);
        }
    }

    updateSortByOptions () {
        const labels = this.config.sortByLabels || {};
        let options = window.algoliaShopify?.sortOrders;

        if (Array.isArray(options) && options.length > 0 && this.sortOptionTemplate && this.sortByContent) {
            options = options.map((option) => normalizeSortOption(option));
            const activeCurrency = normalizeCurrency(this.activeCurrency || getActiveCurrency());
            const activeCurrencyLower = activeCurrency ? activeCurrency.toLowerCase() : "";
            const currentMarket = window.poAlgoliaUtils?.getCurrentMarket?.();
            const country = getActiveCountry() || window.Shopify?.country;
            const selectedRadio = this.form?.elements?.namedItem("sort_by");
            const currentSelection = selectedRadio?.value || this.lastIndex;

            // Always keep only the 4 allowed sort options, but pick the correct index for the
            // active country/currency/market.
            options = narrowSortOrdersForPlp(options, {
                country,
                activeCurrencyLower,
                currentMarket,
            });

            poPlpFiltersSortDebug("updateSortByOptions", {
                activeCurrency,
                country,
                currentMarket: currentMarket?.suffix || null,
                currentSelection,
                lastIndex: this.lastIndex,
                options: options.map((option) => ({
                    label: option?.label || "",
                    value: option?.value || "",
                })),
            });

            const getCanonicalKey = (opt) => {
                const v = normalizeSortValue(opt?.value);
                const l = normalizeSortLabel(opt?.label);

                if (isPriceAscValue(v) || l.includes("cheapest")) return "cheapest";
                if (isPriceDescValue(v) || l.includes("most_expensive")) return "most_expensive";
                if (l === "most_popular" || l.includes("popular") || v.includes("recently_ordered_count_desc") || v.includes("best_seller") || v.includes("newreleases")) {
                    return "most_popular";
                }
                return "relevance";
            };

            const desiredFromSelection = (() => {
                const sel = String(currentSelection || "").toLowerCase();
                if (isPriceAscValue(sel)) return "cheapest";
                if (isPriceDescValue(sel)) return "most_expensive";
                if (sel.includes("recently_ordered_count_desc") || sel.includes("most_popular") || sel.includes("newreleases") || sel.includes("best_seller")) return "most_popular";
                return "relevance";
            })();

            const validSelection = options.some((opt) => opt.value === currentSelection);
            if (validSelection) {
                this.lastIndex = currentSelection;
            } else {
                const desiredOpt = options.find((opt) => getCanonicalKey(opt) === desiredFromSelection);
                this.lastIndex = desiredOpt?.value || options[0]?.value || null;
            }

            this.sortByContent.querySelectorAll("label").forEach(label => label.remove());

            options.forEach(option => {
                const clone = this.sortOptionTemplate.content.cloneNode(true);

                const input = clone.querySelector("input");
                const labelEl = clone.querySelector("[data-sort-label]");
                const normalizedValue = String(option.value || "").toLowerCase();
                const shouldStripCurrency =
                    !!activeCurrencyLower &&
                    (normalizedValue.includes("pricing_") || normalizedValue.includes("market_pricing") || normalizedValue.includes("marketpricing") || normalizedValue.includes(".price_"));
                const normalizedLabel = option.label?.toLowerCase() || "";
                const optLabelRaw = shouldStripCurrency
                    ? normalizedLabel
                        .replace(`(${activeCurrencyLower})`, "")
                        .trim()
                        .replaceAll(" ", "_")
                    : normalizedLabel.replaceAll(" ", "_");
                const canonicalKey = getCanonicalKey(option);
                const labelText = labels[optLabelRaw] || labels[canonicalKey] || option.label;

                if (input) {
                    input.value = option.value;
                    input.name = "sort_by";
                    input.checked = this.lastIndex === option.value;
                    input.ariaLabel = labelText || "";
                    input.dataset.optLabel = canonicalKey;
                }

                if (labelEl) {
                    labelEl.textContent = labelText;
                }

                this.sortByContent.appendChild(clone);
            });

            poPlpFiltersSortDebug("renderedSortOptions", {
                selectedValue: this.lastIndex,
                options: getRenderedSortOptionsSnapshot(this.sortByContent),
            });
        }
    }
});
})();
